import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Input } from '@/components/ui/input';
import { BookOpen, History } from 'lucide-react';
import api from '@/lib/api';
import { formatIST } from '@/lib/utils';
import { getProductTypeName } from '@/lib/constants';

/**
 * The Item cell: type a product name, get the catalogue and what has been
 * ordered before, as one list.
 *
 * It behaves the way a search box behaves — it opens on focus with what this
 * customer usually orders, narrows as you type, and takes the arrow keys — and
 * it does that whether or not the order was started as a repeat. Picking a
 * suggestion carries its price across and links the line to its catalogue entry,
 * so the job card later opens on the specification already worked out. A name
 * that matches nothing is not an error: it is a new product, and it joins the
 * catalogue when the order is placed.
 *
 * The list is drawn on `document.body` rather than in the cell. The items grid
 * scrolls sideways, and a scroll container clips what overflows it in both
 * directions — so a list of eight products showed as one row wedged under the
 * table. Anchored to the input's own rectangle, it lies over the grid instead.
 */
export default function ProductNameCell({
  value, customerId, onPick, placeholder, testId,
  className = 'h-7 text-sm border-0 shadow-none focus-visible:ring-1 px-1', autoFocus = false,
}) {
  const [matches, setMatches] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [anchor, setAnchor] = useState(null);
  const cellRef = useRef(null);
  const query = useRef(value);
  query.current = value;

  const measure = useCallback(() => {
    const box = cellRef.current?.getBoundingClientRect();
    if (box) setAnchor({ left: box.left, top: box.bottom + 2, width: box.width });
  }, []);

  // the anchor has to follow the input: the page scrolls, and so does the grid
  // the input sits in
  useEffect(() => {
    if (!open) return undefined;
    measure();
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
    };
  }, [open, measure]);

  useEffect(() => {
    if (!open) { setMatches([]); return; }
    const term = (value || '').trim();
    const timer = setTimeout(() => {
      api.get('/catalog/suggest', { params: { search: term, customer_id: customerId || undefined, limit: 8 } })
        .then(res => {
          // a slow response for an older keystroke must not replace a newer list
          if ((query.current || '').trim() === term) {
            setMatches(res.data.suggestions || []);
            setActive(-1);
          }
        })
        .catch(() => setMatches([]));
    }, term ? 200 : 0);
    return () => clearTimeout(timer);
  }, [value, open, customerId]);

  const choose = (item) => {
    onPick({
      item_name: item.name,
      catalog_item_id: item.catalog_item_id || null,
      catalog_price_source: item.price_source || null,
      // what is free, not what is on the rack: the rest is promised to orders
      // that have not left yet
      catalog_stock: item.available_stock ?? item.finished_goods_stock ?? 0,
      fulfill_from_stock: false,
      ...(item.price_per_piece != null
        ? { price_type: 'per_piece', price_per_piece: String(item.price_per_piece) }
        : {}),
    });
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (!open || matches.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive(i => (i + 1) % matches.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive(i => (i <= 0 ? matches.length - 1 : i - 1));
    } else if (e.key === 'Enter' && active >= 0) {
      e.preventDefault();
      choose(matches[active]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const typed = (value || '').trim();
  const exact = matches.some(m => m.name.toLowerCase() === typed.toLowerCase());

  const list = open && anchor && (matches.length > 0 || typed.length >= 2) && createPortal(
    <div
      className="fixed z-50 max-h-56 overflow-y-auto rounded border border-slate-200 bg-white shadow-lg divide-y"
      style={{ left: anchor.left, top: anchor.top, width: Math.max(anchor.width, 240) }}
      role="listbox"
    >
      {!typed && matches.length > 0 && (
        <p className="px-2 py-1 text-[10px] uppercase tracking-wider text-slate-400">
          Usually ordered
        </p>
      )}
      {matches.map((item, idx) => (
        <button
          key={item.catalog_item_id || item.name}
          type="button"
          role="option"
          aria-selected={idx === active}
          onMouseDown={() => choose(item)}
          onMouseEnter={() => setActive(idx)}
          className={`w-full px-2 py-1.5 text-left flex items-center justify-between gap-2 ${
            idx === active ? 'bg-slate-100' : 'hover:bg-slate-50'
          }`}
        >
          <span className="min-w-0">
            <span className="block truncate text-sm text-slate-700">{item.name}</span>
            <span className="block truncate text-[10px] text-slate-400">
              {item.product_type ? getProductTypeName(item.product_type, true) : 'Type not set'}
              {item.customer_name ? ` · ${item.customer_name}` : ''}
              {item.available_stock ? ` · ${item.available_stock} in stock` : ''}
              {item.times_ordered > 0 && (
                ` · ordered ${item.times_ordered}×${
                  item.last_ordered_at ? `, last ${formatIST(item.last_ordered_at, { dateOnly: true })}` : ''
                }`
              )}
            </span>
          </span>
          <span className="flex shrink-0 items-center gap-1.5">
            {!item.in_catalog && (
              <History className="h-3 w-3 text-slate-300" aria-label="From a past order" />
            )}
            {item.price_per_piece != null && (
              <span className="text-[11px] tabular-nums text-slate-500">₹{item.price_per_piece}/pc</span>
            )}
          </span>
        </button>
      ))}
      {!exact && typed.length >= 2 && (
        <p className="px-2 py-1.5 text-[10px] text-slate-400">
          <BookOpen className="mr-1 inline h-2.5 w-2.5" />
          “{typed}” is new — it joins the catalogue when the order is placed.
        </p>
      )}
    </div>,
    document.body,
  );

  return (
    <div className="relative w-full min-w-0" ref={cellRef}>
      <Input
        type="text"
        value={value}
        onChange={e => onPick({
          item_name: e.target.value, catalog_item_id: null,
          catalog_stock: 0, fulfill_from_stock: false,
        })}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        className={className}
        autoFocus={autoFocus}
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        data-testid={testId}
      />
      {list}
    </div>
  );
}
